import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col } from "reactstrap";
import NeoAlert from "../../../components/Common/NeoAlert";
import NeoButton from "../../../components/Common/NeoButton";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import usePlatformOnboarding from "./hooks/usePlatformOnboarding";
import { ONBOARDING_STATUS_LABELS, ONBOARDING_STATUS_COLORS, getProgressPercentage } from "./constants";
import "./PlatformOnboarding.css";

const PlatformOnboarding = () => {
    const { items, selected, selectedId, loading, setSelectedId, toggleStep } = usePlatformOnboarding();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Pré-seleciona o onboarding quando chega com ?tenant= (ex.: vindo do CRM ou do detalhe do cliente)
    const tenantParam = searchParams.get("tenant");
    useEffect(() => {
        if (!tenantParam || items.length === 0) return;
        const match = items.find(o => o.idTenant === tenantParam || o.id === tenantParam);
        if (match) setSelectedId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantParam, items]);

    if (loading) {
        return (
            <div className="pb-5">
                <div className="mb-4"><Breadcrumb title="Onboarding" /></div>
                <div className="text-center py-5 text-muted">Carregando onboardings...</div>
            </div>
        );
    }

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Onboarding" />
            </div>

            <Row className="g-4">
                <Col lg={5}>
                    <NeoCard title="Academias em Implantação" headerIcon="mdi-clipboard-text-play-outline" thinBorder bodyClassName="p-4" className="h-100">
                        <div className="onboarding-list-container">
                            {items.map((on, idx) => {
                                const progress = getProgressPercentage(on.steps);
                                const isSelected = selectedId === on.id || selected?.id === on.id;
                                return (
                                    <div
                                        key={on.id}
                                        className={`onboarding-item-card ${isSelected ? "selected" : ""}`}
                                        onClick={() => setSelectedId(on.id)}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                            <div className="d-flex align-items-center gap-2 min-width-0">
                                                <div className="onboarding-number-badge">
                                                    {idx + 1}
                                                </div>
                                                <h6 className="fw-bold text-dark m-0 fs-6 text-truncate">
                                                    {on.tenantName || on.schoolName}
                                                </h6>
                                            </div>
                                            <NeoBadge color={ONBOARDING_STATUS_COLORS[on.status] || "muted"}>
                                                {ONBOARDING_STATUS_LABELS[on.status] || on.status}
                                            </NeoBadge>
                                        </div>

                                        <div className="onboarding-meta-info mb-3">
                                            <div className="onboarding-meta-item">
                                                <i className="ph-light ph-calendar" />
                                                <span>
                                                    Início: {on.startDate ? new Date(on.startDate).toLocaleDateString("pt-BR") : "—"}
                                                </span>
                                            </div>
                                            <div className="onboarding-meta-item">
                                                <i className="ph-light ph-user-circle" />
                                                <span>
                                                    CSM: {on.csmName || on.manager || "Não atribuído"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <div className="progress flex-grow-1 csm-progress-container">
                                                <div
                                                    className={`progress-bar ${progress === 100 ? "csm-progress-bar-success" : "csm-progress-bar-brand"}`}
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <span className="font-size-11 fw-bold text-dark">{progress}%</span>
                                        </div>
                                    </div>
                                );
                            })}
                            {items.length === 0 && (
                                <div className="text-center py-4 text-muted text-sm">Nenhum onboarding em andamento.</div>
                            )}
                        </div>
                    </NeoCard>
                </Col>

                <Col lg={7}>
                    {selected ? (
                        <NeoCard
                            title={`Checklist de Implantação: ${selected.tenantName || selected.schoolName}`}
                            subtitle="Marque as etapas executadas para monitorar a ativação da academia."
                            headerIcon="mdi-clipboard-text-outline"
                            headerActions={
                                <NeoButton
                                    variant="soft-primary"
                                    size="sm"
                                    onClick={() => navigate(`/platform/tenants/${selected.idTenant || selected.id}`)}
                                >
                                    <i className="mdi mdi-office-building-outline me-1" />
                                    Ver Cliente
                                </NeoButton>
                            }
                            thinBorder
                            bodyClassName="p-4"
                            className="h-100"
                        >
                            {selected.status === "ready" && (
                                <NeoAlert color="success" className="border-0 shadow-sm mb-4" animate={false}>
                                    <strong>Tudo pronto!</strong> Esta academia completou 100% das etapas de implantação.
                                </NeoAlert>
                            )}

                            <div className="platform-checklist">
                                {(selected.steps || []).map((step) => (
                                    <div
                                        key={step.id}
                                        className={`platform-checklist-item ${step.checked ? "checked" : ""}`}
                                        onClick={() => toggleStep(selected.id, step.id)}
                                    >
                                        <div className="platform-checklist-content">
                                            <div className="platform-checklist-checkbox-wrapper">
                                                <i className="mdi mdi-check" />
                                            </div>
                                            <label className="platform-checklist-label">{step.label}</label>
                                        </div>
                                        <NeoBadge color={step.checked ? "success" : "muted"}>
                                            {step.checked ? "Concluído" : "Aguardando"}
                                        </NeoBadge>
                                    </div>
                                ))}
                            </div>
                        </NeoCard>
                    ) : (
                        <NeoCard thinBorder bodyClassName="p-4" className="h-100">
                            <div className="text-center py-5 text-muted">Selecione um onboarding para ver o checklist.</div>
                        </NeoCard>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default PlatformOnboarding;
